import type { ConfigManager } from '../core/config';
import type { TranslateFunction } from '../types';
import { HttpClient } from './http-client';
import { TokenManager } from './token-manager';
import { LanguageService } from './language';

interface TranslateResponseData {
  success: boolean;
  data?: {
    translationList: Array<{
      index: string;
      translation: string;
      code?: number;
    }>;
  };
}

export class TranslateService {
  private httpClient: HttpClient;
  private tokenManager: TokenManager | null;
  private customTranslate: TranslateFunction | null;
  private config: ConfigManager;
  private languageService: LanguageService;

  constructor(config: ConfigManager, languageService: LanguageService) {
    this.config = config;
    this.languageService = languageService;
    this.httpClient = new HttpClient();

    if (config.translate) {
      this.customTranslate = config.translate;
      this.tokenManager = null;
    } else if (config.getToken) {
      this.customTranslate = null;
      this.tokenManager = new TokenManager(config.getToken);
    } else {
      this.customTranslate = null;
      this.tokenManager = null;
    }
  }

  async translate(
    texts: string[],
    srcLang: string,
    tgtLang: string,
    _format: 'text' | 'html' = 'text',
  ): Promise<string[]> {
    if (texts.length === 0) return [];

    const maxRetries = this.config.maxRetries;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const results = this.customTranslate
          ? await this.translateWithCustom(texts, attempt > 0)
          : await this.translateWithToken(texts, srcLang, tgtLang);

        this.fireAfterTranslate(texts, results);
        return results;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        if (attempt >= maxRetries) break;
      }
    }

    throw lastError ?? new Error('Translation failed');
  }

  private async translateWithToken(
    texts: string[],
    srcLang: string,
    tgtLang: string,
  ): Promise<string[]> {
    if (!this.tokenManager) {
      throw new Error('TokenManager not available. Provide getToken in SDK config.');
    }

    const effectiveTgt = tgtLang || this.languageService.getDefaultTarget(srcLang);
    const textMap = texts.reduce<Record<string, string>>(
      (acc, text, i) => ({ ...acc, [String(i)]: text }), {},
    );

    const signedRequest = await this.tokenManager.getSignedRequest({
      sourceLanguage: srcLang,
      targetLanguage: effectiveTgt,
      streaming: false,
      text: textMap,
      scene: 'mt-turbo',
      fallbackTimeoutMs: 10000,
      appName: 'tongyi_translate_jssdk',
      ext: {
        terminologies: this.config.terminologies.length > 0 ? this.config.terminologies : undefined,
        examples: this.config.examples.length > 0 ? this.config.examples : undefined,
        domainHint: this.config.domainHint,
      },
    });

    const response = await this.httpClient.executeSignedRequest<TranslateResponseData>(signedRequest);

    if (!response.success || !response.data) {
      throw new Error(response.error ?? 'Translation request failed');
    }

    const data = response.data;
    if (!data.success || !data.data?.translationList) {
      throw new Error('Invalid translation response');
    }

    const sorted = data.data.translationList.sort(
      (a, b) => Number(a.index) - Number(b.index),
    );

    return sorted.map((item, i) => item.translation || texts[i]);
  }

  private async translateWithCustom(texts: string[], isRetry: boolean): Promise<string[]> {
    if (!this.customTranslate) {
      throw new Error('Custom translate function not available');
    }
    return this.customTranslate(texts, { type: 'page', isRetry });
  }

  private fireAfterTranslate(sources: string[], targets: string[]): void {
    const callback = this.config.afterTranslate;
    if (!callback) return;

    try {
      callback({
        sources,
        targets,
        requestId: crypto.randomUUID(),
      });
    } catch (e) {
      console.error('[TranslateService] afterTranslate callback error:', e);
    }
  }

  destroy(): void {
    this.httpClient.abortAll();
  }
}
